import { useTranslation } from "react-i18next"

import type { AdminTableSort, AdminTableStatus } from "../hooks/use-admin-table-controls"

import { Search } from "@/components/icons"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

export function AdminTableToolbar({
  controls,
  bulkActions = false,
  showFilters = true,
}: {
  showFilters?: boolean
  bulkActions?: boolean
  controls: {
    search: string
    setSearch: (value: string) => void
    status: AdminTableStatus
    setStatus: (value: AdminTableStatus) => void
    sort: AdminTableSort
    setSort: (value: AdminTableSort) => void
    count: number
    total: number
  }
}) {
  const { t } = useTranslation()
  return (
    <div className="space-y-2">
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        {showFilters && (
          <>
            <div className="relative min-w-0 flex-1 sm:min-w-48">
              <Search
                aria-hidden="true"
                className="text-muted-foreground pointer-events-none absolute top-3 left-3 size-4"
              />
              <Input
                aria-label={t("adminDashboard.tableControls.search", "Search rows")}
                className="h-10 pl-9"
                placeholder={t("adminDashboard.tableControls.search", "Search rows")}
                value={controls.search}
                onChange={(event) => controls.setSearch(event.target.value)}
              />
            </div>
            <Select
              value={controls.status}
              onValueChange={(value) => controls.setStatus(value as AdminTableStatus)}
            >
              <SelectTrigger
                aria-label={t("adminDashboard.tableControls.status", "Filter by status")}
                className="h-10 w-full sm:w-40"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("adminDashboard.tableControls.all", "All statuses")}
                </SelectItem>
                <SelectItem value="enabled">
                  {t("adminDashboard.tableControls.enabled", "Enabled")}
                </SelectItem>
                <SelectItem value="disabled">
                  {t("adminDashboard.tableControls.disabled", "Disabled")}
                </SelectItem>
              </SelectContent>
            </Select>
          </>
        )}
        <Select
          value={controls.sort}
          onValueChange={(value) => controls.setSort(value as AdminTableSort)}
        >
          <SelectTrigger
            aria-label={t("adminDashboard.tableControls.sort", "Sort rows")}
            className="h-10 w-full sm:w-44"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="original">
              {t("adminDashboard.tableControls.original", "Default order")}
            </SelectItem>
            <SelectItem value="name-asc">
              {t("adminDashboard.tableControls.nameAsc", "Name: A–Z")}
            </SelectItem>
            <SelectItem value="name-desc">
              {t("adminDashboard.tableControls.nameDesc", "Name: Z–A")}
            </SelectItem>
            <SelectItem value="enabled-first">
              {t("adminDashboard.tableControls.enabledFirst", "Enabled first")}
            </SelectItem>
            <SelectItem value="disabled-first">
              {t("adminDashboard.tableControls.disabledFirst", "Disabled first")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <p aria-live="polite" className="text-muted-foreground text-xs">
        {t("adminDashboard.tableControls.count", "{{count}} of {{total}} rows match", {
          count: controls.count,
          total: controls.total,
        })}
      </p>
      {bulkActions && (controls.search.trim() || controls.status !== "all") ? (
        <p className="text-muted-foreground text-xs">
          {t(
            "adminDashboard.tableControls.bulkScope",
            "Bulk actions apply to all items, including filtered-out rows."
          )}
        </p>
      ) : null}
    </div>
  )
}
